import React, { useState, useRef, useEffect } from 'react';
import { Blurhash } from 'react-blurhash';

interface VideoPlayerProps {
  videoUrl: string;
  thumbnailUrl: string;
  blurhash?: string;
  autoPlay?: boolean;
  controls?: boolean;
  style?: React.CSSProperties;
  className?: string;
  onLoadStart?: () => void;
  onLoadEnd?: () => void;
  onError?: (error: any) => void;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  videoUrl,
  thumbnailUrl,
  blurhash,
  autoPlay = false,
  controls = true,
  style = {},
  className = '',
  onLoadStart,
  onLoadEnd,
  onError
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [videoReady, setVideoReady] = useState(false);

  // Always use a valid blurhash - default if none provided or invalid
  const validBlurhash = blurhash && blurhash.length >= 6 
    ? blurhash 
    : 'LEHV6nWB2yk8pyo0adR*.7kCMdnj';

  console.log('VideoPlayer - Using blurhash:', validBlurhash);

  useEffect(() => {
    // Reset state when video URL changes
    setLoading(true);
    setError(null);
    setVideoReady(false);
  }, [videoUrl]);

  const handleCanPlay = () => {
    setVideoReady(true);
    setLoading(false);
    if (onLoadEnd) onLoadEnd();
  };

  const handleError = (e: any) => {
    setError(new Error('Video could not be loaded'));
    setLoading(false);
    if (onError) onError(e);
  };

  const handleLoadStart = () => {
    setLoading(true);
    if (onLoadStart) onLoadStart();
  };

  return (
    <div 
      style={{ 
        position: 'relative',
        width: '100%',
        height: '100%',
        backgroundColor: '#000',
        ...style
      }}
      className={className}
    >
      {/* Blurhash placeholder - shown whenever video is not ready */}
      {!videoReady && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1,
            overflow: 'hidden'
          }}
        >
          <Blurhash
            hash={validBlurhash}
            width="100%"
            height="100%"
            resolutionX={32}
            resolutionY={32}
            punch={1}
          />
        </div>
      )}

      {/* Loading indicator shown over the blurhash */}
      {loading && (
        <div 
          style={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.3)'
          }}
        >
          <div 
            style={{ 
              width: '50px', 
              height: '50px', 
              border: '4px solid rgba(255,255,255,0.2)',
              borderRadius: '50%',
              borderTopColor: 'rgba(255,255,255,0.7)',
              animation: 'spin 1s linear infinite'
            }} 
          />
          <style>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}

      {/* Video element */}
      <video
        ref={videoRef}
        src={videoUrl}
        controls={controls}
        autoPlay={autoPlay}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          backgroundColor: '#000',
          opacity: videoReady ? 1 : 0, // Hide video until ready
          zIndex: 2
        }}
        onCanPlay={handleCanPlay}
        onError={handleError}
        onLoadStart={handleLoadStart}
        playsInline
      />

      {/* Error display */}
      {error && (
        <div 
          style={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: '#fff',
            padding: '20px',
            textAlign: 'center'
          }}
        >
          <div>
            <p>Video konnte nicht geladen werden</p>
            <button
              onClick={() => window.open(videoUrl, '_blank')}
              style={{
                padding: '8px 16px',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                marginTop: '10px'
              }}
            >
              Video direkt öffnen
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoPlayer; 